import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import { State } from '../../../vista-usuario/vista-usuario.state';
import { Subscription, combineLatest } from 'rxjs';
import { selectAllEntidadesProductiva } from '../../../empresas/entity-entidad-productiva/entity-entidades-productiva.selectors';
import { TramiteTableColumnModel } from '../../utils/tramite-table-columns.util';
import { EntidadesProductiva } from '../../../empresas/entity-entidad-productiva/entity-entidades-productiva.model';
import { PersonasFisica } from '../../../empresas/entity-personas-fisica/entity-personas-fisica.model';
import { selectAllPersonasFisica } from '../../../empresas/entity-personas-fisica/entity-personas-fisica.selectors';
import { selectAllPersonas } from '../../../persona/entity-personas/entity-personas.selectors';
import { Persona } from '@rpe/registro-personas-empresas-lib';
import { PersonasJuridica } from '../../../empresas/entity-personas-juridica/entity-personas-juridica.model';
import { selectAllPersonasJuridica } from '../../../empresas/entity-personas-juridica/entity-personas-juridica.selectors';
import { Tramite } from '../../../tramites/tramite.models';
import { selectTramitesFromCurrentEmpresa } from '../../../tramites/tramite.selectors';
import { TramiteStatus } from '../../../tramites/tramite-status/tramite-status.model';
import { selectAllTramiteStatus } from '../../../tramites/tramite-status/tramite-status.selectors';
import { selectAllMovimientoTramites } from '../../../tramites/movimiento-tramite/movimiento-tramite.selectors';
import { MovimientoTramite } from '../../../tramites/movimiento-tramite/movimiento-tramite.models';

@Component({
  selector: 'rpe-vista-tramite-container',
  templateUrl: './vista-tramite-container.component.html',
  styleUrls: ['./vista-tramite-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VistaTramiteContainerComponent implements OnInit {
  subscription: Subscription;

  tramiteTableData: TramiteTableColumnModel[] = [];

  entidadesProductiva: EntidadesProductiva[] = [];
  tramites: Tramite[] = [];
  personas: Persona[] = [];
  personasFisica: PersonasFisica[] = [];
  personasJuridica: PersonasJuridica[] = [];
  tramiteStatus: TramiteStatus[] = [];
  movimientoTramite: MovimientoTramite[] = [];

  constructor(private store: Store<State>, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.subscription = combineLatest([
      this.store.pipe(select(selectTramitesFromCurrentEmpresa)),
      this.store.pipe(select(selectAllEntidadesProductiva)),
      this.store.pipe(select(selectAllPersonasFisica)),
      this.store.pipe(select(selectAllPersonas)),
      this.store.pipe(select(selectAllPersonasJuridica)),
      this.store.pipe(select(selectAllTramiteStatus))
    ]).subscribe(
      ([
        tramites,
        entidadesProductiva,
        personaFisica,
        personas,
        personaJuridica,
        tramiteStatus
      ]) => {
        this.tramites = tramites;
        this.entidadesProductiva = entidadesProductiva;
        this.personasFisica = personaFisica;
        this.personas = personas as unknown as Persona[];
        this.personasJuridica = personaJuridica;
        this.tramiteStatus = tramiteStatus;

        this.populateTableData();

        this.cdr.detectChanges(); // Fuerzo actualizar input child
      }
    );

    this.store.pipe(select(selectAllMovimientoTramites)).subscribe((mt) => {
      this.movimientoTramite = mt;

      this.populateTableData();

      this.cdr.detectChanges(); // Fuerzo actualizar input child
    });
  }

  populateTableData(): void {
    this.tramiteTableData = this.tramites.map((tramite: Tramite) => {
      const movimientoTramite = this.getMovimientoTramiteByTramiteId(
        tramite.id
      );

      return {
        id: tramite.id,
        empresa: this.nombreEmpresa(
          this.getEntidadProductiva(tramite.entidad_relacion_id)
        ),
        estado: this.tramiteStatusById(tramite.estado_id).estado_nombre,
        entidadProductivaId: tramite.entidad_relacion_id,
        description: movimientoTramite?.observacion || 'No tiene'
      } as unknown as TramiteTableColumnModel;
    });
  }

  getEntidadProductiva(entidadProductivaId: number): EntidadesProductiva {
    return this.entidadesProductiva.find((ep) => ep.id === entidadProductivaId);
  }

  personaFisicaByEntidadProductiva(
    entidadProductiva: EntidadesProductiva
  ): PersonasFisica {
    return this.personasFisica.find(
      (pf) => pf.id === entidadProductiva.persona_fisica_id
    );
  }

  personaById(personaId: number): Persona {
    return this.personas.find((p) => p.id === personaId);
  }

  personaJuridicaById(personaId: number): PersonasJuridica {
    return this.personasJuridica.find((pj) => pj.id === personaId);
  }

  tramiteStatusById(tramiteId: number): TramiteStatus {
    return this.tramiteStatus.find((ts) => ts.estado_id === tramiteId);
  }

  nombreEmpresa(entidadProductiva: EntidadesProductiva): string {
    if (entidadProductiva.persona_fisica_id) {
      return this.personaById(
        this.personaFisicaByEntidadProductiva(entidadProductiva).persona_id
      ).nombre_completo;
    }

    if (entidadProductiva.persona_juridica_id) {
      return this.personaJuridicaById(entidadProductiva.persona_juridica_id)
        .denominacion_social;
    }

    return 'La empresa no tiene nombre';
  }

  getMovimientoTramiteByTramiteId(tramiteId: number): MovimientoTramite {
    const movimentoTramite = this.movimientoTramite
      .filter((mt) => mt.tramite_id === tramiteId)
      .sort((a, b) => {
        return b.id - a.id;
      })[0];

    return movimentoTramite;
  }
}
